import React from 'react';

interface ApiCardProps {
  title: string;
  path: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  description: string;
  parameters?: Array<{
    name: string;
    in: string;
    type: string;
  }>;
}

interface GitCommitGenieCardProps {
  title: string;
  description: string;
  technologies: string[];
  onLaunch?: () => void;
  status?: 'ready' | 'in-progress';
  buttonText?: string;
}

export const ApiCard: React.FC<ApiCardProps> = ({
  title,
  path,
  method,
  description,
  parameters = []
}) => {
  const methodColor = {
    GET: 'bg-green-100 dark:bg-gray-600 text-green-800 dark:text-gray-100',
    POST: 'bg-blue-100 dark:bg-gray-600 text-blue-800 dark:text-gray-100',
    PUT: 'bg-yellow-100 dark:bg-gray-600 text-yellow-800 dark:text-gray-100',
    DELETE: 'bg-red-100 dark:bg-gray-600 text-red-800 dark:text-gray-100'
  }[method];

  return (
    <div className="border dark:border-gray-700 rounded-lg p-4 mb-4 shadow-sm hover:shadow-md transition-shadow bg-white dark:bg-gray-800">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-lg font-semibold dark:text-white">{title}</h3>
        <span className={`px-2 py-1 rounded text-xs font-medium ${methodColor}`}>
          {method}
        </span>
      </div>
      
      <div className="mb-3">
        <code className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-sm dark:text-gray-300">{path}</code>
      </div>
      
      <p className="text-gray-600 dark:text-gray-300 mb-3">{description}</p>
      
      {parameters.length > 0 && (
        <div>
          <h4 className="text-sm font-medium mb-1 dark:text-gray-300">Parameters:</h4>
          <ul className="space-y-1">
            {parameters.map((param, index) => (
              <li key={index} className="text-sm dark:text-gray-300">
                <span className="font-medium">{param.name}</span> ({param.in}) - {param.type}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export const GitCommitGenieCard: React.FC<GitCommitGenieCardProps> = ({
  title = "Git Commit Genie",
  description = "Paste a `git diff` and get AI-suggested conventional commit messages.",
  technologies = ["React", "C# / .NET", "AI API"],
  onLaunch,
  status = 'ready',
  buttonText = 'Launch App'
}) => {
  // Function to determine the style for each technology tag
  const getTechTagStyle = (tech: string) => {
    if (tech.toLowerCase().includes("react")) {
      return "bg-green-100 dark:bg-gray-600 text-green-800 dark:text-gray-100";
    } else if (tech.toLowerCase().includes("c#") || tech.toLowerCase().includes(".net")) {
      return "bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-100";
    } else if (tech.toLowerCase().includes("ai api")) {
      return "bg-purple-100 dark:bg-gray-600 text-purple-700 dark:text-gray-100";
    } else if (tech.toLowerCase().includes("hugging face")) {
      return "bg-yellow-100 dark:bg-gray-600 text-yellow-800 dark:text-gray-100";
    } else if (tech.toLowerCase().includes("database")) {
      return "bg-red-100 dark:bg-gray-600 text-red-800 dark:text-gray-100";
    } else {
      return "bg-blue-100 dark:bg-gray-600 text-blue-700 dark:text-gray-100";
    }
  };

  // Function to get the appropriate icon based on the card title
  const getCardIcon = () => {
    const titleLower = title.toLowerCase();
    
    if (titleLower.includes('git commit')) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
        </svg>
      );
    } else if (titleLower.includes('explain')) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>
      );
    } else if (titleLower.includes('text-to-study')) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      );
    } else if (titleLower.includes('jingle')) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
        </svg>
      );
    } else if (titleLower.includes('sentiment')) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      );
    } else if (titleLower.includes('resume')) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      );
    } else if (titleLower.includes('math')) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      );
    } else if (titleLower.includes('journal')) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      );
    } else if (titleLower.includes('link')) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
        </svg>
      );
    } else if (titleLower.includes('pantry') || titleLower.includes('chef') || titleLower.includes('recipe')) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
        </svg>
      );
    } else if (titleLower.includes('market') || titleLower.includes('stock')) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      );
    } else if (titleLower.includes('legal')) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
        </svg>
      );
    } else if (titleLower.includes('hr') || titleLower.includes('career')) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      );
    } else {
      // Default icon for any other cards
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      );
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 h-full flex flex-col transform transition-all duration-300 hover:scale-[1.03] hover:shadow-lg active:scale-[0.96]">
      <div className="flex items-center mb-2">
        {getCardIcon()}
        <h2 className="text-xl font-bold text-gray-800 dark:text-white ml-2">{title}</h2>
      </div>
      
      <p className="text-gray-600 dark:text-gray-300 mb-4 flex-grow">{description}</p>
      
      <div className="flex flex-wrap gap-2 mb-4">
        {technologies.map((tech, index) => (
          <span 
            key={index}
            className={`px-2 py-1 rounded-full text-xs font-medium ${getTechTagStyle(tech)}`}
          >
            {tech}
          </span>
        ))}
      </div>
      
      <button 
        onClick={onLaunch}
        disabled={status === 'in-progress'}
        className={`w-full font-medium py-2 px-4 rounded-md transition-colors ${status === 'in-progress' ? 'bg-gray-400 text-white cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
      >
        {status === 'in-progress' ? 'Work In Progress' : buttonText}
      </button>
    </div>
  );
};
